import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dailymedAPI } from '../services/api';

function DailyMedDetail() {
  const { setid } = useParams();
  const navigate = useNavigate();
  const [label, setLabel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLabelDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await dailymedAPI.getLabel(setid);
        if (response.data) {
          setLabel(response.data);
        } else {
          setError('Drug label not found');
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };

    if (setid) {
      fetchLabelDetails();
    }
  }, [setid]);

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="loading-spinner"></div>
          <p>Loading drug label...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="error-message">
          <p>⚠️ Error: {error}</p>
          <button onClick={() => navigate('/dailymed')} className="btn-primary">
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  if (!label) {
    return (
      <div className="page-container">
        <div className="error-message">
          <p>Drug label not found</p>
          <button onClick={() => navigate('/dailymed')} className="btn-primary">
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <button 
        onClick={() => navigate(-1)} 
        className="back-button"
        style={{
          marginBottom: '1rem',
          padding: '0.5rem 1rem',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#e9ecef';
          e.target.style.borderColor = '#adb5bd';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#f8f9fa';
          e.target.style.borderColor = '#dee2e6';
        }}
      >
        <span>←</span> Back to Search
      </button>

      <div style={{
        background: 'linear-gradient(135deg, #16a085 0%, #27ae60 100%)',
        color: 'white',
        padding: '2rem',
        borderRadius: '8px',
        marginBottom: '2rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold' }}>
          {label.title || 'Drug Label Information'}
        </h1>
        {label.labeler && (
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.1rem', opacity: 0.95 }}>
            Manufactured by: {label.labeler}
          </p>
        )}
      </div>

      <div style={{
        backgroundColor: '#e7f3ff',
        border: '1px solid #b3d9ff',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '2rem',
        display: 'flex',
        gap: '1rem',
        alignItems: 'start'
      }}>
        <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>ℹ️</span>
        <div style={{ fontSize: '0.95rem', color: '#004085' }}>
          <strong>Note:</strong> This page displays basic drug label information from DailyMed. 
          For complete prescribing information, warnings, drug interactions, clinical studies, 
          patient counseling information, and full labeling details, please visit the 
          <a 
            href={`https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=${setid}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              color: '#0056b3', 
              marginLeft: '0.25rem',
              fontWeight: 'bold',
              textDecoration: 'underline'
            }}
          >
            official DailyMed source
          </a>.
        </div>
      </div>

      <div className="detail-section">
        <h2 className="section-title" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          fontSize: '1.5rem',
          color: '#2c3e50'
        }}>
          📋 Label Information
        </h2>
        <div style={{ 
          backgroundColor: 'white',
          border: '1px solid #e1e8ed',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <div style={{ 
            display: 'grid',
            gap: '0'
          }}>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: '200px 1fr',
              padding: '1rem',
              borderBottom: '1px solid #e1e8ed',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
            >
              <span style={{ 
                fontWeight: '600',
                color: '#495057',
                fontSize: '0.95rem'
              }}>Set ID</span>
              <span style={{ 
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                color: '#212529',
                wordBreak: 'break-all'
              }}>
                {label.setid}
              </span>
            </div>
            
            {label.published_date && (
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: '200px 1fr',
                padding: '1rem',
                borderBottom: '1px solid #e1e8ed',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                <span style={{ 
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.95rem'
                }}>Published Date</span>
                <span style={{ 
                  color: '#212529',
                  fontSize: '0.95rem'
                }}>
                  {label.published_date}
                </span>
              </div>
            )}
            
            {label.version_number && (
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: '200px 1fr',
                padding: '1rem',
                borderBottom: label.effective_time || label.author ? '1px solid #e1e8ed' : 'none',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                <span style={{ 
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.95rem'
                }}>Version Number</span>
                <span style={{ 
                  color: '#212529',
                  fontSize: '0.95rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    backgroundColor: '#16a085',
                    color: 'white',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold'
                  }}>
                    v{label.version_number}
                  </span>
                </span>
              </div>
            )}
            
            {label.effective_time && (
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: '200px 1fr',
                padding: '1rem',
                borderBottom: label.author ? '1px solid #e1e8ed' : 'none',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                <span style={{ 
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.95rem'
                }}>Effective Time</span>
                <span style={{ 
                  color: '#212529',
                  fontSize: '0.95rem'
                }}>
                  {label.effective_time}
                </span>
              </div>
            )}
            
            {label.author && (
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: '200px 1fr',
                padding: '1rem',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                <span style={{ 
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.95rem'
                }}>Author</span>
                <span style={{ 
                  color: '#212529',
                  fontSize: '0.95rem'
                }}>
                  {label.author}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {(label.product_type || label.marketing_category || label.application_number) && (
        <div className="detail-section">
          <h2 className="section-title" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            fontSize: '1.5rem',
            color: '#2c3e50'
          }}>
            🏛️ Regulatory Information
          </h2>
          <div style={{ 
            backgroundColor: 'white',
            border: '1px solid #e1e8ed',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <div style={{ display: 'grid', gap: '0' }}>
              {label.application_number && (
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: '200px 1fr',
                  padding: '1.25rem',
                  backgroundColor: '#f0f9ff',
                  borderBottom: (label.product_type || label.marketing_category) ? '1px solid #e1e8ed' : 'none'
                }}>
                  <span style={{ 
                    fontWeight: '600',
                    color: '#495057',
                    fontSize: '0.95rem',
                    display: 'flex',
                    alignItems: 'center'
                  }}>FDA Application Number</span>
                  <span style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}>
                    <span style={{
                      fontFamily: 'monospace',
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      color: '#0066cc',
                      backgroundColor: 'white',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      border: '2px solid #0066cc',
                      letterSpacing: '0.5px'
                    }}>
                      {label.application_number}
                    </span>
                    <span style={{
                      fontSize: '0.75rem',
                      color: '#6c757d',
                      backgroundColor: '#e9ecef',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px'
                    }}>
                      FDA Approved
                    </span>
                  </span>
                </div>
              )}
              
              {label.product_type && (
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: '200px 1fr',
                  padding: '1rem',
                  borderBottom: label.marketing_category || label.approved_date ? '1px solid #e1e8ed' : 'none',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <span style={{ 
                    fontWeight: '600',
                    color: '#495057',
                    fontSize: '0.95rem'
                  }}>Product Type</span>
                  <span style={{ 
                    color: '#212529',
                    fontSize: '0.95rem'
                  }}>
                    {label.product_type}
                  </span>
                </div>
              )}
              
              {label.marketing_category && (
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: '200px 1fr',
                  padding: '1rem',
                  borderBottom: label.approved_date ? '1px solid #e1e8ed' : 'none',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <span style={{ 
                    fontWeight: '600',
                    color: '#495057',
                    fontSize: '0.95rem'
                  }}>Marketing Category</span>
                  <span style={{ 
                    fontFamily: 'monospace',
                    color: '#212529',
                    fontSize: '0.9rem'
                  }}>
                    {label.marketing_category}
                  </span>
                </div>
              )}
              
              {label.approved_date && (
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: '200px 1fr',
                  padding: '1rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <span style={{ 
                    fontWeight: '600',
                    color: '#495057',
                    fontSize: '0.95rem'
                  }}>Approved Date</span>
                  <span style={{ 
                    color: '#212529',
                    fontSize: '0.95rem'
                  }}>
                    {new Date(label.approved_date).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {label.active_ingredients && label.active_ingredients.length > 0 && (
        <div className="detail-section">
          <h2 className="section-title">Active Ingredients</h2>
          <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
            <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
              {label.active_ingredients.map((ingredient, i) => (
                <li key={i} style={{ padding: '0.5rem 0' }}>
                  <strong>{ingredient.name}</strong>
                  {ingredient.strength && ` - ${ingredient.strength}`}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {label.route && label.route.length > 0 && (
        <div className="detail-section">
          <h2 className="section-title">Routes of Administration</h2>
          <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {(Array.isArray(label.route) ? label.route : [label.route]).map((route, index) => (
                <span key={index} style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#16a085',
                  color: 'white',
                  borderRadius: '20px',
                  fontSize: '0.9rem'
                }}>
                  {route}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {label.ndc && label.ndc.length > 0 && (
        <div className="detail-section">
          <h2 className="section-title">
            NDC Codes 
            {label.total_ndcs && label.total_ndcs > label.ndc.length && (
              <span style={{ fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem', color: '#666' }}>
                (Showing {label.ndc.length} of {label.total_ndcs})
              </span>
            )}
          </h2>
          <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '0.5rem',
              fontFamily: 'monospace', 
              fontSize: '0.85rem' 
            }}>
              {label.ndc.map((ndc, i) => (
                <div 
                  key={i} 
                  style={{ 
                    padding: '0.5rem', 
                    backgroundColor: 'white',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    textAlign: 'center'
                  }}
                >
                  {ndc}
                </div>
              ))}
            </div>
            {label.total_ndcs && label.total_ndcs > label.ndc.length && (
              <div style={{ 
                marginTop: '1rem', 
                padding: '0.75rem', 
                backgroundColor: '#e7f3ff',
                borderRadius: '4px',
                fontSize: '0.9rem',
                color: '#004085'
              }}>
                ℹ️ This drug has {label.total_ndcs} total NDC codes. Visit the DailyMed source below to view all codes.
              </div>
            )}
          </div>
        </div>
      )}

      {label.dosage_form && (
        <div className="detail-section">
          <h2 className="section-title">Dosage Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Dosage Form:</span>
              <span className="info-value">{label.dosage_form}</span>
            </div>
            {label.strength && (
              <div className="info-item">
                <span className="info-label">Strength:</span>
                <span className="info-value">{label.strength}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {label.warnings && (
        <div className="detail-section">
          <h2 className="section-title">Warnings</h2>
          <div style={{ 
            backgroundColor: '#fff3cd', 
            padding: '1rem', 
            borderRadius: '4px',
            borderLeft: '4px solid #ffc107'
          }}>
            <div dangerouslySetInnerHTML={{ __html: label.warnings }} />
          </div>
        </div>
      )}

      {label.indications_and_usage && (
        <div className="detail-section">
          <h2 className="section-title">Indications and Usage</h2>
          <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
            <div dangerouslySetInnerHTML={{ __html: label.indications_and_usage }} />
          </div>
        </div>
      )}

      {label.dosage_and_administration && (
        <div className="detail-section">
          <h2 className="section-title">Dosage and Administration</h2>
          <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
            <div dangerouslySetInnerHTML={{ __html: label.dosage_and_administration }} />
          </div>
        </div>
      )}

      <div className="detail-section">
        <h2 className="section-title" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          fontSize: '1.5rem',
          color: '#2c3e50'
        }}>
          🔗 Source Information
        </h2>
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #e1e8ed',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <div style={{ 
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <div>
              <div style={{ 
                fontWeight: '600',
                color: '#495057',
                fontSize: '0.95rem',
                marginBottom: '0.5rem'
              }}>
                Official DailyMed Database
              </div>
              <div style={{ 
                fontSize: '0.85rem',
                color: '#6c757d'
              }}>
                Access complete prescribing information and full drug label
              </div>
            </div>
            <a 
              href={`https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=${setid}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                backgroundColor: '#16a085',
                color: 'white',
                textDecoration: 'none',
                fontWeight: '600',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s ease',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.95rem',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#138f75';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#16a085';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
            >
              <span>View Full Label on DailyMed</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
            </a>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '2rem', textAlign: 'center' }}>
        <button 
          onClick={() => navigate(-1)} 
          className="btn-secondary"
          style={{
            padding: '0.75rem 2rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '1rem',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#5a6268'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
        >
          ← Back to Search Results
        </button>
      </div>
    </div>
  );
}

export default DailyMedDetail;
